import clsx from "clsx";
import { ArrowLeftRight, Check, Compass, Info, RotateCcw, TriangleAlert } from "lucide-react";
import { type KeyboardEvent, useEffect, useId, useState } from "react";

import type { Deployment } from "../../api/types";
import { designate, designateSoon } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Checkbox } from "../../components/Checkbox";
import { Spinner } from "../../components/EmptyState";
import { PanelRow } from "../../components/panel";
import { Motion, Presence } from "../../components/presence";
import { Badge, Button, inputClass } from "../../components/ui";
import { formatNumber, k, lang, t, tc } from "../../i18n/i18n";
import { capLabel, shiftPhrase } from "../../map/manoeuvre/useManoeuvreWrites";
import {
  angleGap,
  directionName,
  fitFor,
  formatBearing,
  nearestPossible,
  normalizeDeg,
  parseBearing,
  rumb16,
  spans,
  toMils,
} from "../../map/manoeuvre/orientation";
import { type OrientationView, useOrientationView } from "../../map/manoeuvre/orientationView";
import { attention, ORIENTATION_ATTENTION, useApplyOrientation } from "../../map/manoeuvre/useApplyOrientation";
import { useOrientation } from "../../map/manoeuvre/useOrientation";
import { useUi } from "../../state/ui";
import { orientationRow } from "./manoeuvreModel";
import { Waiting } from "../../components/Waiting";

/** Sens de la manœuvre AMI → ENI (docs/14 §2.14), dans l'étape « Terrain » du panneau Manœuvre : la ligne du sens
 *  (cap, rumb, millièmes, état en pastille) et la carte d'orientation (rose, champ d'azimut, inversion, sens par
 *  défaut, graphiques, effets annoncés, sens impossible expliqué avec l'action attendue, Appliquer / Annuler). La
 *  flèche et l'anneau sont dessinés par la carte (`OrientationOverlay`). Composants partagés seulement. */

const language = (): "fr" | "en" => (lang() === "en" ? "en" : "fr");

/** Cible de 44 px au doigt (critère 9) sans changer la hauteur dessinée du bouton (gabarit Prevail) : la zone
 *  sensible s'étend au-dessus et au-dessous par un pseudo-élément, seulement sur un écran tactile. */
const TOUCH_SM = "relative pointer-coarse:after:absolute pointer-coarse:after:inset-x-0 pointer-coarse:after:-inset-y-2";
const TOUCH_MD = "relative pointer-coarse:after:absolute pointer-coarse:after:inset-x-0 pointer-coarse:after:-inset-y-1.5";
/** Lien et case à cocher (texte de 12 px, environ 18 px de haut) : 14 px au-dessus et au-dessous, soit 46 px ; les
 *  liens du Callout sont espacés d'autant au doigt pour que leurs zones ne se chevauchent pas. */
const TOUCH_LINK = "relative pointer-coarse:after:absolute pointer-coarse:after:inset-x-0 pointer-coarse:after:-inset-y-3.5";

/** « 800 millièmes · NE » (« 800 mils · NE »). */
function milsLine(deg: number): string {
  return t("{mils} millièmes · {rumb}", { mils: formatNumber(toMils(deg)), rumb: rumb16(deg, language()) });
}

/** Ligne « Sens AMI → ENI : 045° (NE) » de l'étape Terrain (camp et terrain libre). */
export function OrientationRow({ deployment, onRedrawAxis }: { deployment: Deployment; onRedrawAxis?: () => void }) {
  const row = orientationRow(deployment);
  if (!row) return null;
  const title = t("Sens AMI → ENI : {cap}", { cap: capLabel(row.bearing) });
  if (row.state === "zone") {
    return (
      <PanelRow
        icon={<Compass size={14} />}
        title={title}
        detail={t("Fixé par l'axe tracé")}
        actions={
          onRedrawAxis ? (
            <Button variant="link" onClick={onRedrawAxis}>
              {t("Retracer l'axe")}
            </Button>
          ) : undefined
        }
      />
    );
  }
  const template = row.template ?? row.bearing;
  const mils = formatNumber(toMils(row.bearing));
  const notApplied = row.tangled
    ? t("Fuseau invalide une fois recalé sur le terrain dans ce sens : sens par défaut {cap} utilisé", { cap: formatBearing(template) })
    : t("Ne tient pas dans le camp pour cette mission : sens par défaut {cap} utilisé", { cap: formatBearing(template) });
  const detail =
    row.state === "not_applied"
      ? notApplied
      : row.state === "overflow"
        ? t("Le dispositif déborde du camp dans ce sens")
        : row.state === "oriented"
          ? t("{mils} millièmes · sens par défaut {cap}", { mils, cap: capLabel(template) })
          : t("{mils} millièmes · sens par défaut", { mils });
  const badge =
    row.state === "oriented" ? (
      <Badge dot tone="accent">
        {t("Orienté")}
      </Badge>
    ) : row.state === "not_applied" ? (
      <Badge dot tone="warn">
        {t("Non appliqué")}
      </Badge>
    ) : row.state === "overflow" ? (
      <Badge dot tone="warn">
        {t("Déborde")}
      </Badge>
    ) : undefined;
  return <PanelRow data-orientation-row="" icon={<Compass size={14} />} title={title} detail={detail} state={badge} />;
}

/** Ce qui empêche d'ouvrir le mode orientation, dans l'ordre où l'action attendue est désignée : dossier non
 *  enregistré (« Enregistrer »), retouche du fuseau en cours (« Terminé »), fuseau antérieur aux largeurs du mémento
 *  (« Revenir au fuseau automatique »), graphique en cours de tracé (son bandeau) ; null : rien. */
export type OrientationBlock = "save" | "editing" | "legacy" | "draft";

export function orientationBlock(state: {
  canEdit: boolean;
  editing: boolean;
  legacy: boolean;
  draft: boolean;
}): OrientationBlock | null {
  if (!state.canEdit) return "save";
  if (state.editing) return "editing";
  if (state.legacy) return "legacy";
  if (state.draft) return "draft";
  return null;
}

/** Cible désignée et avis de chaque blocage (le dossier non enregistré passe par `guardWrites` du panneau). */
export const ORIENTATION_BLOCKS: Record<Exclude<OrientationBlock, "save">, { attention: string; notice: string }> = {
  editing: { attention: "manoeuvre-done", notice: k("Terminez d'abord la retouche du fuseau") },
  legacy: {
    attention: "manoeuvre-corridor-reset",
    notice: k("Fuseau tracé avant les largeurs doctrinales : revenez au fuseau automatique pour orienter la manœuvre"),
  },
  draft: { attention: "draft-banner", notice: k("Terminez d'abord le graphique en cours") },
};

/** Ligne CENTAC du bloc d'information : l'axe se règle dans les Données initiales (champ désigné). */
export function CentacAxisNote() {
  const setView = useUi((s) => s.setView);
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-ink-muted">{t("L'axe de manœuvre se règle dans les Données initiales.")}</span>
      <Button
        variant="link"
        onClick={() => {
          setView("mission");
          // La vue entre en glissé : la cible est cherchée jusqu'à ce qu'elle soit montée (appareil lent compris).
          designateSoon(attention("dossier-centac-axis"), "ring");
        }}
      >
        {t("Régler l'axe de manœuvre")}
      </Button>
    </div>
  );
}

const ROSE_LABEL: Record<number, string> = {
  0: k("Nord ({cap})"),
  45: k("Nord-est ({cap})"),
  90: k("Est ({cap})"),
  135: k("Sud-est ({cap})"),
  180: k("Sud ({cap})"),
  225: k("Sud-ouest ({cap})"),
  270: k("Ouest ({cap})"),
  315: k("Nord-ouest ({cap})"),
};

/** Rose : NO N NE / O [champ] E / SO S SE ; cibles de 44 px au doigt. Boutons à bascule (`aria-pressed`) dessinés
 *  comme ceux des temps de la DIREX (`GenerateMenu`) : `Button` ne porte pas d'état enfoncé. */
const ROSE_GRID: (number | "field")[] = [315, 0, 45, 270, "field", 90, 225, 180, 135];

/** Ce qui rend le sens visé impossible, en phrases (Callout « Hors du camp »). */
function blockers(view: OrientationView): string[] {
  const reason = view.reason;
  if (!reason) return [];
  const out: string[] = [];
  if (reason.blocker === "content") {
    const s = spans(view.model, view.target);
    const km = (m: number) => formatNumber(m / 1000, { maximumFractionDigits: 1 });
    const nsOver = s.ns - s.campNs;
    const ewOver = s.ew - s.campEw;
    if (nsOver >= ewOver) {
      out.push(t("Dans ce sens, le dispositif mesure {a} km du nord au sud ; le camp en offre {b}.", { a: km(s.ns), b: km(s.campNs) }));
    } else {
      out.push(t("Dans ce sens, le dispositif mesure {a} km d'ouest en est ; le camp en offre {b}.", { a: km(s.ew), b: km(s.campEw) }));
    }
  } else if (reason.blocker === "hand") {
    out.push(
      reason.count === 1
        ? t("1 unité posée à la main sortirait du camp.")
        : t("{n} unités posées à la main sortiraient du camp.", { n: reason.count }),
    );
  } else if (reason.blocker === "graphics") {
    out.push(
      reason.count === 1
        ? t("1 graphique tactique sortirait du camp.")
        : t("{n} graphiques tactiques sortiraient du camp.", { n: reason.count }),
    );
  } else {
    out.push(t("Le fuseau retouché sortirait du camp."));
  }
  const ranges = view.arcs
    .filter(([a, b]) => !(a === 0 && b === 359))
    .map(([a, b]) => t("{from} à {to}", { from: formatBearing(a), to: formatBearing(b) }));
  if (ranges.length) out.push(t("Sens possibles : {ranges}.", { ranges: joinList(ranges) }));
  return out;
}

/** « a, b et c » (« a, b and c »). */
function joinList(items: string[]): string {
  if (items.length < 2) return items.join("");
  return `${items.slice(0, -1).join(", ")} ${t("et")} ${items[items.length - 1]}`;
}

/** Effets annoncés du sens visé (seulement ceux qui s'appliquent). */
function effects(view: OrientationView): string[] {
  if (view.unchanged) return [];
  const out = [view.custom ? t("Le fuseau retouché tourne d'un bloc ; ses LIMA gardent leur place") : t("Le fuseau automatique sera recalé sur le terrain")];
  const shift = shiftPhrase(fitFor(view.model, view.target).shift);
  if (shift) out.push(shift);
  if (view.handUnits === 1) out.push(t("1 unité posée à la main tourne avec la manœuvre"));
  else if (view.handUnits > 1) out.push(t("{n} unités posées à la main tournent avec la manœuvre", { n: view.handUnits }));
  return out;
}

/** Carte d'orientation, sous la rangée de boutons de l'étape Terrain (mode ouvert). */
export function OrientationCard({ onFreeTerrain, freeOffered }: { onFreeTerrain?: () => void; freeOffered?: boolean }) {
  const view = useOrientationView();
  const target = useOrientation((s) => s.target);
  const setTarget = useOrientation((s) => s.setTarget);
  const graphics = useOrientation((s) => s.graphics);
  const setGraphics = useOrientation((s) => s.setGraphics);
  const pending = useOrientation((s) => s.pending);
  const cancel = useOrientation((s) => s.cancel);
  const notify = useUi((s) => s.notify);
  const { apply } = useApplyOrientation();
  const fieldId = useId();
  const [text, setText] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState(false);
  // Le champ suit le sens visé tant qu'il n'est pas en cours de saisie.
  useEffect(() => setText(null), [target]);
  if (!view || target === null) return null;
  const lang2 = language();
  const possible = view.feasibility.possible;
  const commitField = () => {
    if (text === null) return;
    const parsed = parseBearing(text);
    if (parsed === null) {
      setFieldError(true);
      designate(`#${CSS.escape(fieldId)}`, "field");
      notify({ text: t("Azimut de 0 à 359°, ou de 0 à 6 399 millièmes"), tone: "warn" });
      return;
    }
    setFieldError(false);
    setText(null);
    setTarget(normalizeDeg(parsed));
  };
  const onFieldKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitField();
    } else if (e.key === "Escape" && text !== null) {
      e.preventDefault();
      e.stopPropagation();
      setText(null);
      setFieldError(false);
    }
  };
  const aim = (deg: number) => {
    if (!possible[deg % 360]) {
      notify({ text: t("Hors du camp à {cap}", { cap: capLabel(deg) }), tone: "warn" });
      setTarget(deg);
      // Le Callout apparaît au rendu qui suit : son action est désignée une fois affichée.
      designateSoon(attention(ORIENTATION_ATTENTION.calloutAction), "ring", 600);
      return;
    }
    setTarget(deg);
  };
  const nearest = view.reason ? nearestPossible(possible, view.target) : null;
  const reasons = blockers(view);
  const announced = effects(view);
  const template = view.model.template;
  const graphicsBlock = view.reason?.blocker === "graphics";

  return (
    <div data-orientation-card="" className="mx-2 mb-2 space-y-2 rounded-lg border border-line p-2">
      {/* 1. Rose et champ central. */}
      <div
        role="group"
        aria-label={t("Sens de la manœuvre")}
        data-attention={ORIENTATION_ATTENTION.rose}
        className="mx-auto grid w-max grid-cols-3 gap-1 rounded-lg p-0.5 pointer-coarse:gap-1.5"
      >
        {ROSE_GRID.map((cell) =>
          cell === "field" ? (
            <div key="field" className="relative w-24 self-center">
              <input
                id={fieldId}
                aria-label={t("Azimut")}
                aria-invalid={fieldError || undefined}
                inputMode="numeric"
                autoComplete="off"
                className={clsx(inputClass, "pr-5 text-center font-mono tabular-nums pointer-coarse:h-11", fieldError && "border-danger")}
                value={text ?? String(Math.round(view.target) % 360).padStart(3, "0")}
                onChange={(e) => {
                  setText(e.target.value);
                  setFieldError(false);
                }}
                onBlur={commitField}
                onKeyDown={onFieldKey}
              />
              <span aria-hidden className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-ui text-ink-muted">
                °
              </span>
            </div>
          ) : (
            <button
              key={cell}
              type="button"
              aria-pressed={angleGap(view.target, cell) < 0.5}
              aria-disabled={possible[cell] ? undefined : true}
              aria-label={t(ROSE_LABEL[cell]!, { cap: formatBearing(cell) })}
              title={possible[cell] ? undefined : t("Ne tient pas dans le camp dans ce sens")}
              onClick={() => aim(cell)}
              className={clsx(
                "flex h-8 w-12 items-center justify-center rounded-lg border text-ui font-medium transition-colors pointer-coarse:h-11 pointer-coarse:w-14",
                angleGap(view.target, cell) < 0.5 ? "border-accent bg-accent-soft text-accent" : "border-line-strong bg-surface text-ink",
                possible[cell] ? "hover:bg-hover" : "opacity-40",
              )}
            >
              {rumb16(cell, lang2)}
            </button>
          ),
        )}
      </div>
      {/* 2. Millièmes et rumb. */}
      <p className="text-center text-meta text-ink-muted">{milsLine(view.target)}</p>
      {/* 3. Inverser, sens par défaut. */}
      <div className="flex flex-wrap justify-center gap-2">
        <Button size="sm" variant="secondary" className={TOUCH_SM} onClick={() => setTarget(normalizeDeg(view.target + 180))}>
          <ArrowLeftRight size={14} /> {t("Inverser le sens")}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          className={TOUCH_SM}
          title={t("Sens par défaut de la mission sur ce camp : {cap}", { cap: capLabel(template) })}
          onClick={() => setTarget(template)}
        >
          <RotateCcw size={14} /> {t("Sens par défaut")}
        </Button>
      </div>
      {/* 4. Graphiques tactiques (masqué sans graphique). */}
      <Presence show={view.graphicsTotal > 0}>
        <Motion origin="fade" className="flex min-h-8 items-center px-1 py-1.5 pointer-coarse:py-3.5">
          <Checkbox
            checked={graphics}
            onChange={setGraphics}
            className={TOUCH_LINK}
            label={t("Faire tourner aussi les graphiques tactiques ({n})", { n: view.graphicsTotal })}
          />
        </Motion>
      </Presence>
      {/* 5. Effets annoncés. */}
      <Presence show={announced.length > 0 && !view.reason}>
        <Motion origin="fade" className="space-y-px">
          {announced.map((line) => (
            <PanelRow key={line} icon={<Info size={14} />} title={<span className="font-normal text-ink-muted">{line}</span>} />
          ))}
        </Motion>
      </Presence>
      {/* 6. Sens impossible : la cause, les sens possibles, puis les actions sous le texte (la colonne d'action du
          Callout réduirait l'explication à quelques caractères dans les 380 px du panneau). */}
      <Presence show={Boolean(view.reason)}>
        <Callout tone="warn" title={t("Hors du camp à {cap}", { cap: capLabel(view.target) })}>
          <p>{reasons.join(" ")}</p>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 pointer-coarse:mt-3.5 pointer-coarse:gap-y-7">
            {nearest !== null && (
              <Button
                variant="link"
                className={TOUCH_LINK}
                data-attention={ORIENTATION_ATTENTION.calloutAction}
                onClick={() => setTarget(nearest)}
              >
                {t("Prendre le sens possible le plus proche ({cap})", { cap: formatBearing(nearest) })}
              </Button>
            )}
            {graphicsBlock && (
              <Button variant="link" className={TOUCH_LINK} onClick={() => setGraphics(false)}>
                {t("Ne pas faire tourner les graphiques")}
              </Button>
            )}
            {freeOffered && onFreeTerrain && view.reason?.blocker === "content" && (
              <Button variant="link" className={TOUCH_LINK} onClick={onFreeTerrain}>
                {t("Construire sur un terrain libre")}
              </Button>
            )}
          </div>
        </Callout>
      </Presence>
      {/* 7. Appliquer, Annuler. */}
      <div className="flex flex-wrap items-center gap-2 py-2.5">
        <Button
          data-attention={ORIENTATION_ATTENTION.apply}
          className={TOUCH_MD}
          disabled={pending}
          blocked={!pending && (view.unchanged || Boolean(view.reason))}
          onBlocked={() => apply(view)}
          onClick={() => apply(view)}
        >
          {pending ? <Spinner decorative /> : <Check size={14} />} {pending ? <Waiting>{t("Calage sur le terrain…")}</Waiting> : t("Appliquer")}
        </Button>
        <Button variant="ghost" className={TOUCH_MD} disabled={pending} onClick={cancel}>
          {t("Annuler")}
        </Button>
        <Presence show={Boolean(view.reason)}>
          <Motion origin="fade" className="flex items-center gap-1 text-meta text-warn">
            <TriangleAlert size={12} aria-hidden /> {t("Hors du camp")}
          </Motion>
        </Presence>
      </div>
      <p className="sr-only" aria-live="polite">
        {t("Sens visé : {cap}, {direction}", { cap: formatBearing(view.target), direction: directionLabel(view.target) })}
        {view.reason ? `, ${t("hors du camp")}` : ""}
      </p>
    </div>
  );
}

/** Nom de la direction de la rose la plus proche, dans la langue de l'interface (annonce du sens visé). */
export function directionLabel(deg: number): string {
  switch (directionName(deg, "fr")) {
    case "nord-est":
      return tc("direction", "nord-est");
    case "est":
      return tc("direction", "est");
    case "sud-est":
      return tc("direction", "sud-est");
    case "sud":
      return tc("direction", "sud");
    case "sud-ouest":
      return tc("direction", "sud-ouest");
    case "ouest":
      return tc("direction", "ouest");
    case "nord-ouest":
      return tc("direction", "nord-ouest");
    default:
      return tc("direction", "nord");
  }
}
